import { useRef, useState } from 'react';

import Button from '@mui/material/Button';

import {
  StyledStack,
  StyledStackWrapper,
} from '@webclient/components/cropper/styles';
import CONFIG from '@webclient/components/workspace/setup/config';

import 'cropperjs/dist/cropper.css';

import { Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';
import ReactCropper, { ReactCropperElement } from 'react-cropper';

const CROPPER_HEIGHT = 200;
const CROPPER_WIDTH = 284;

type Kind = 'image' | 'icon';

type Props = {
  uploadedAvatar: File | null;
  selectedIcon?: string;
  handleGetCroppedImage: (args: {
    data: Blob;
    name: string;
    type: string;
  }) => void;
  Header?: React.ReactNode;
  Footer?: React.ReactNode;
};

const INIT_SIZE = 250;
const INIT_QUALITY = 50;

export default function Cropper({
  selectedIcon,
  uploadedAvatar,
  handleGetCroppedImage,
  Footer,
  Header,
}: Props) {
  const { t } = useTranslate();
  const whatIsUserPreferance: Kind = uploadedAvatar ? 'image' : 'icon';
  const cropperRef = useRef<ReactCropperElement>(null);
  const [cropper, setCropper] = useState<string>();

  function handleCrop(type: Kind) {
    const cropper = cropperRef.current?.cropper;
    return () => {
      if (type === 'image') {
        const dataUrl = cropper
          ?.getCroppedCanvas()
          .toDataURL(CONFIG.AVATAR.FORMAT, CONFIG.AVATAR.QUALITY);
        setCropper(dataUrl);
        cropper?.getCroppedCanvas().toBlob(
          async (blob) => {
            if (!blob || !uploadedAvatar) return;
            handleGetCroppedImage({
              data: blob,
              name: uploadedAvatar.name,
              type: CONFIG.AVATAR.FORMAT,
            });
          },
          CONFIG.AVATAR.FORMAT,
          CONFIG.AVATAR.QUALITY,
        );
        return;
      }

      const dataUrl = cropper
        ?.getCroppedCanvas()
        .toDataURL(CONFIG.AVATAR.FORMAT, CONFIG.AVATAR.QUALITY);

      setCropper(dataUrl);
    };
  }

  function handleInitCropper(type: Kind) {
    return (_instance: Cropper) => {
      //? upload an imane
      if (type === 'image' && uploadedAvatar) {
        setCropper(URL.createObjectURL(uploadedAvatar));
        return;
      }

      //? select an icon
      setCropper(
        getLowResolutionImageUrl({
          url: selectedIcon!,
          width: INIT_SIZE,
          height: INIT_SIZE,
          quality: INIT_QUALITY,
        }),
      );
    };
  }

  return (
    <>
      {Header}
      <StyledStackWrapper>
        <StyledStack gap={2.5}>
          <Text
            variant="textMd"
            weight="semibold"
          >
            {t(
              'page.workspace.createWorkspaceModal.step2.subSteps.step3.title',
              'Crop Image',
            )}
          </Text>
          <ReactCropper
            checkCrossOrigin={false}
            onInitialized={handleInitCropper(whatIsUserPreferance)}
            src={cropper}
            style={{ height: CROPPER_HEIGHT, width: CROPPER_WIDTH }}
            initialAspectRatio={16 / 16}
            ref={cropperRef}
            crossOrigin="anonymous"
          />
          <Button
            fullWidth
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleCrop(whatIsUserPreferance)}
          >
            {t(
              'page.workspace.createWorkspaceModal.step2.subSteps.step3.buttons.apply',
              'Apply',
            )}
          </Button>
        </StyledStack>
      </StyledStackWrapper>
      {Footer}
    </>
  );
}

function getLowResolutionImageUrl({
  url,
  width,
  height,
  quality,
}: {
  url: string;
  width: number;
  height: number;
  quality: number;
}) {
  const urlObj = new URL(url);
  urlObj.searchParams.set('width', String(width));
  urlObj.searchParams.set('height', String(height));
  urlObj.searchParams.set('quality', String(quality));
  return urlObj.toString();
}
